import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="grid min-h-screen place-items-center bg-slate-50 p-6">
      <div className="w-full max-w-sm space-y-6 rounded-xl border bg-white p-8 shadow-sm fade-up">
        <div className="flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-lg bg-indigo-600 font-display text-lg font-bold text-white">A</div>
          <span className="font-display text-xl font-semibold">Aspire</span>
        </div>
        <div>
          <h2 className="font-display text-2xl font-semibold">{title}</h2>
          {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
        </div>
        {children}
      </div>
    </div>
  );
}

export default function SetPassword() {
  const [params] = useSearchParams();
  const t = params.get("t") || "";
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/auth/password-token", { params: { t } }).then((r) => setInfo(r.data)).catch((e) => setErr(errMsg(e)));
  }, [t]);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return setErr("Passwords don't match");
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/set-password", { token: t, password: pw });
      toast.success("Password set — please sign in");
      nav("/login", { replace: true });
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  if (!info && !err) return <AuthShell title="Checking your link…"><Loader2 className="h-5 w-5 animate-spin text-indigo-600" /></AuthShell>;
  if (!info) {
    return (
      <AuthShell title="Link not valid">
        <div data-testid="set-password-invalid" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>
        <Link to="/forgot-password" className="text-sm font-medium text-indigo-600 hover:underline" data-testid="request-new-link">Request a new link</Link>
      </AuthShell>
    );
  }
  return (
    <AuthShell title={info.purpose === "welcome" ? `Welcome, ${info.name}` : "Choose a new password"} subtitle={`For ${info.email}. At least 8 characters, including a number or symbol.`}>
      <form onSubmit={submit} className="space-y-4" data-testid="set-password-form">
        <div className="space-y-1.5"><Label>New password</Label><Input data-testid="set-password-new" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8} /></div>
        <div className="space-y-1.5"><Label>Confirm password</Label><Input data-testid="set-password-confirm" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required /></div>
        {err && <div data-testid="set-password-error" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
        <Button data-testid="set-password-submit" type="submit" className="w-full" disabled={busy}>{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}Set password</Button>
      </form>
    </AuthShell>
  );
}
